import { clientPage } from '@/lib/client-pages'
import { text } from '@/lib/search-params'

/**
 * Le chemin d'où l'on vient (#224). Une page de détail ouverte depuis un
 * onglet de la fiche client (ou d'un projet, ou du tableau de bord) le porte
 * dans sa recherche, `?retour=`, pour que « Retour » ramène **là**, avec le
 * même onglet et les mêmes filtres — pas à la liste globale de l'objet. Dans
 * l'URL, donc survivant à un rechargement ; transmis tel quel d'une page de
 * détail à la suivante, donc jamais empilé.
 */
export const RETURN_KEY = 'retour'

/** À étaler dans le schéma d'URL de toute route de détail. */
export const RETURN_SHAPE = { retour: text() }

const MAX_LENGTH = 2000

/**
 * N'accepte qu'un chemin **relatif à l'origine** : un `/` seul en tête, pas
 * de `//` (adresse sans schéma), pas de schéma, pas de retour à la ligne.
 * Une valeur venue d'un lien extérieur ne fait naviguer nulle part.
 */
export function parseReturnTo(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (value.length === 0 || value.length > MAX_LENGTH) return null
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null
  if (/[\r\n]/.test(value)) return null
  const beforeQuery = value.split(/[?#]/, 1)[0] ?? ''
  if (beforeQuery.includes(':')) return null
  return value
}

/** Le chemin courant tel qu'on le rejoue : chemin et recherche, sans l'origine. */
export function locationPath(location: { pathname: string; searchStr: string }): string {
  return `${location.pathname}${location.searchStr}`
}

/** Les anciens onglets (`?tab=`), pour un `retour` écrit avant #285. */
const LEGACY_CLIENT_TABS: Record<string, string> = { audit: 'journal' }

const PROJECT_TAB_LABELS: Record<string, string> = {
  apercu: 'Aperçu',
  sprints: 'Sprints',
  documents: 'Documents',
}

const LIST_LABELS: Record<string, string> = {
  '/': 'Retour au tableau de bord',
  '/clients': 'Retour aux clients',
  '/opportunities': 'Retour aux affaires',
  '/quotes': 'Retour aux soumissions',
  '/echanges': 'Retour aux échanges',
  '/relances': 'Retour aux relances',
  '/projects': 'Retour aux projets',
  '/tasks': 'Retour aux tâches',
  '/time': 'Retour au temps',
  '/frais': 'Retour aux notes de frais',
  '/memoire': 'Retour à la mémoire',
  '/audit': 'Retour à l’audit',
  '/qa': 'Retour au tableau de bord QA',
  '/prospecting': 'Retour à la prospection',
  '/questionnaires': 'Retour aux questionnaires',
  '/formations': 'Retour aux formations',
  '/ma-recette': 'Retour à ma recette',
}

export interface ReturnNames {
  /** Le nom du client de l'objet ouvert : « Retour à ACME inc. · Affaires ». */
  client?: string | null
  /** La référence ou le nom du projet de l'objet ouvert. */
  project?: string | null
}

/**
 * Le libellé nomme la destination — jamais « Retour » nu : « Retour à ACME
 * inc. · Affaires », « Retour au projet ACME:SITE · Documents », « Retour au
 * tableau de bord ». Les noms viennent de l'objet ouvert ; le chemin ne porte
 * que des identifiants, et on ne les montre pas.
 */
export function returnToLabel(path: string, names: ReturnNames = {}): string {
  const url = new URL(path, 'http://origine.local')
  const segments = url.pathname.split('/').filter(Boolean)
  const tab = url.searchParams.get('tab')
  const onglet = url.searchParams.get('onglet')

  if (segments.length === 0) return LIST_LABELS['/'] ?? 'Retour'
  if (segments[0] === 'clients' && (segments.length === 2 || segments.length === 3)) {
    // #285 : `/clients/{id}/{page}` ; un `retour` d'avant porte encore `?tab=`.
    const where = names.client ?? 'la fiche client'
    const slug = segments[2] ?? (tab ? (LEGACY_CLIENT_TABS[tab] ?? tab) : undefined)
    const page = clientPage(slug)
    const pageLabel = page && page.slug !== 'apercu' ? page.label : undefined
    return `Retour à ${where}${pageLabel ? ` · ${pageLabel}` : ''}`
  }
  if (segments[0] === 'clients' && segments[2] === 'echanges') return 'Retour à l’échange'
  if (segments[0] === 'projects' && segments.length >= 2) {
    const where = names.project ?? decodeURIComponent(segments[1] ?? '')
    if (segments[2] === 'cahier') {
      return segments.length > 3 ? 'Retour à la section du cahier' : 'Retour au cahier des charges'
    }
    if (segments[2] === 'livrables') {
      return segments.length > 3 ? 'Retour au livrable' : 'Retour aux livrables'
    }
    const tabLabel = tab ? PROJECT_TAB_LABELS[tab] : undefined
    return `Retour au projet ${where}${tabLabel ? ` · ${tabLabel}` : ''}`
  }
  // #401 : la QA — le tableau de bord, la liste, la saisie.
  if (segments[0] === 'qa' && segments[1] === 'bugs') {
    return segments[2] === 'nouveau' ? 'Retour à la saisie' : 'Retour aux bugs'
  }
  if (segments.length === 1) {
    const base = LIST_LABELS[`/${segments[0] ?? ''}`]
    if (base) return onglet === 'rapport' ? 'Retour au rapport' : base
  }
  return 'Retour'
}
